<template>
  <v-dialog
    v-model="dialog"
    max-width="1040px"
    :fullscreen="$vuetify.breakpoint.smAndDown"
    scrollable
  >
    <v-card class="install-status-card">
      <div class="install-status-header primary white--text">
        <div class="install-status-header__icon">
          <v-icon color="white">
            mdi-cellphone-check
          </v-icon>
        </div>
        <div class="install-status-header__text">
          <div class="install-status-header__title">
            App Installation Status
          </div>
          <div class="install-status-header__subtitle">
            View the APKs currently installed on the company's devices.
          </div>
        </div>
        <v-btn
          icon
          dark
          class="install-status-header__close"
          @click="dialog = false"
        >
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </div>

      <v-card-text class="install-status-body">
        <v-container class="pa-0">
          <!-- Filters -->
          <div class="install-status-section pa-5 mb-4">
            <div class="install-status-section-title mb-4">
              Filters
            </div>
            <v-row dense>
              <v-col
                cols="12"
                md="6"
              >
                <v-autocomplete
                  v-model="deviceId"
                  :items="deviceOptions"
                  item-text="displayLabel"
                  item-value="id"
                  label="Device (optional)"
                  placeholder="All devices"
                  :loading="loadingDevices"
                  :disabled="loadingDevices || loadingStatus"
                  clearable
                  hide-details
                />
              </v-col>
              <v-col
                cols="12"
                md="6"
              >
                <v-combobox
                  v-model="packageName"
                  :items="packageOptions"
                  label="Package name (optional)"
                  placeholder="All packages"
                  :loading="loadingApps"
                  :disabled="loadingApps || loadingStatus"
                  clearable
                  hide-details
                />
              </v-col>
            </v-row>
            <div class="d-flex justify-end mt-4">
              <app-btn
                :loading="loadingStatus"
                :disabled="!selectedCompanyId || loadingStatus"
                @click="loadStatus"
              >
                <v-icon left>
                  mdi-magnify
                </v-icon>
                View Status
              </app-btn>
            </div>
          </div>

          <!-- Results -->
          <div
            v-if="hasSearched"
            class="install-status-results"
          >
            <v-progress-linear
              v-if="loadingStatus"
              indeterminate
              color="primary"
              class="mb-4"
            />

            <v-alert
              v-if="!loadingStatus && statusError"
              dense
              text
              type="warning"
            >
              {{ statusError }}
            </v-alert>

            <v-alert
              v-if="!loadingStatus && !statusError && !deviceReports.length"
              dense
              text
              type="info"
            >
              No installation records found for the selected filters.
            </v-alert>

            <div
              v-if="!loadingStatus && !statusError && deviceReports.length"
              class="install-status-toolbar mb-3"
            >
              <v-switch
                v-model="showAllApps"
                inset
                hide-details
                class="mt-0 pt-0"
                :label="showAllApps ? 'Showing all apps' : 'Showing whitelisted apps only'"
              />
              <span class="text-caption grey--text text--darken-1">
                Whitelisted apps (registered for this company) are always listed first.
              </span>
            </div>

            <div
              v-for="report in deviceReports"
              :key="report._key"
              class="install-status-section mb-4"
            >
              <div class="install-status-device pa-4">
                <div class="install-status-device__main">
                  <v-icon
                    color="primary"
                    class="mr-3"
                  >
                    mdi-cellphone
                  </v-icon>
                  <div>
                    <div class="install-status-device__name">
                      {{ report.deviceName || ('Device #' + (report.deviceId || '?')) }}
                    </div>
                    <div class="install-status-device__id">
                      {{ report.macAddress || '-' }}
                    </div>
                  </div>
                </div>
                <div class="install-status-device__meta">
                  <v-chip
                    small
                    outlined
                    color="primary"
                  >
                    {{ report.apps.length }} app{{ report.apps.length === 1 ? '' : 's' }}
                  </v-chip>
                  <div
                    v-if="countWhitelisted(report)"
                    class="install-status-meta"
                  >
                    <v-icon
                      small
                      color="amber darken-2"
                    >
                      mdi-star
                    </v-icon>
                    {{ countWhitelisted(report) }} whitelisted
                  </div>
                  <div
                    v-if="report.reportedAt"
                    class="install-status-meta"
                  >
                    <v-icon small>
                      mdi-clock-outline
                    </v-icon>
                    {{ shortDateTime(report.reportedAt) }}
                  </div>
                </div>
              </div>

              <v-divider />

              <v-data-table
                :headers="appHeaders"
                :items="visibleApps(report)"
                :items-per-page="5"
                item-key="packageName"
                show-expand
                single-expand
                :footer-props="{ 'items-per-page-options': [5, 10, 25] }"
                class="install-status-app-table"
                :no-data-text="showAllApps ? 'No installed APKs reported.' : 'No whitelisted APKs installed.'"
              >
                <template v-slot:[`item.appName`]="{ item }">
                  <div class="d-flex align-center">
                    <v-icon
                      :color="item._whitelisted ? 'amber darken-2' : 'grey lighten-1'"
                      small
                      class="mr-2"
                      :title="item._whitelisted ? 'Whitelisted app' : 'Not whitelisted'"
                    >
                      {{ item._whitelisted ? 'mdi-star' : 'mdi-star-outline' }}
                    </v-icon>
                    <div class="install-status-app-cell">
                      <div class="install-status-app-name">
                        {{ item.appName || item.packageName || '-' }}
                      </div>
                      <div class="install-status-package">
                        {{ item.packageName || '-' }}
                      </div>
                    </div>
                  </div>
                </template>
                <template v-slot:[`item.versionName`]="{ item }">
                  <div class="install-status-ver">
                    <span
                      class="install-status-ver__name"
                      :title="item.versionName || ''"
                    >{{ item.versionName || '-' }}</span>
                    <span
                      v-if="isFiniteNumber(item.versionCode)"
                      class="install-status-ver__code"
                    >{{ item.versionCode }}</span>
                    <v-chip
                      v-if="item.previousVersionName && item.previousVersionName !== item.versionName"
                      x-small
                      color="amber lighten-4"
                      text-color="amber darken-4"
                      class="install-status-ver__chip"
                      :title="'Updated from ' + item.previousVersionName"
                    >
                      <v-icon
                        x-small
                        left
                      >
                        mdi-arrow-up-bold
                      </v-icon>
                      updated
                    </v-chip>
                  </div>
                </template>
                <template v-slot:[`item.lastStatus`]="{ item }">
                  <v-chip
                    small
                    :color="statusColor(item.lastStatus)"
                    text-color="white"
                  >
                    {{ item.lastStatus || 'Unknown' }}
                  </v-chip>
                </template>
                <template v-slot:[`item.reportedAt`]="{ item }">
                  <span class="install-status-reported">{{ item.reportedAt ? shortDateTime(item.reportedAt) : '-' }}</span>
                </template>
                <template v-slot:expanded-item="{ headers, item }">
                  <td
                    :colspan="headers.length"
                    class="install-status-detail"
                  >
                    <div class="install-status-detail-grid">
                      <div class="install-status-detail-row">
                        <span class="install-status-detail__k">Package</span>
                        <span class="install-status-detail__v install-status-package">{{ item.packageName || '-' }}</span>
                      </div>
                      <div class="install-status-detail-row">
                        <span class="install-status-detail__k">Managed</span>
                        <span class="install-status-detail__v">
                          <v-icon
                            small
                            :color="item.isManaged ? 'teal' : 'grey lighten-1'"
                          >
                            {{ item.isManaged ? 'mdi-shield-check' : 'mdi-shield-outline' }}
                          </v-icon>
                          {{ item.isManaged ? 'Yes' : 'No' }}
                        </span>
                      </div>
                      <div
                        v-if="item.lastMessage"
                        class="install-status-detail-row install-status-detail-row--full"
                      >
                        <span class="install-status-detail__k">Message</span>
                        <span class="install-status-detail__v">{{ item.lastMessage }}</span>
                      </div>
                      <div
                        v-if="item.previousVersionName"
                        class="install-status-detail-row"
                      >
                        <span class="install-status-detail__k">Previous version</span>
                        <span class="install-status-detail__v">
                          {{ item.previousVersionName }}
                          <span v-if="isFiniteNumber(item.previousVersionCode)">({{ item.previousVersionCode }})</span>
                        </span>
                      </div>
                      <div
                        v-if="isValidDate(item.firstInstalledAt)"
                        class="install-status-detail-row"
                      >
                        <span class="install-status-detail__k">First installed</span>
                        <span class="install-status-detail__v">{{ $format.dateTime(item.firstInstalledAt) }}</span>
                      </div>
                      <div
                        v-if="isValidDate(item.currentVersionInstalledAt)"
                        class="install-status-detail-row"
                      >
                        <span class="install-status-detail__k">This version installed</span>
                        <span class="install-status-detail__v">{{ $format.dateTime(item.currentVersionInstalledAt) }}</span>
                      </div>
                      <div
                        v-if="isValidDate(item.lastInstalledAt)"
                        class="install-status-detail-row"
                      >
                        <span class="install-status-detail__k">Last installed</span>
                        <span class="install-status-detail__v">{{ $format.dateTime(item.lastInstalledAt) }}</span>
                      </div>
                      <div
                        v-if="item.sourceJobCode"
                        class="install-status-detail-row install-status-detail-row--full"
                      >
                        <span class="install-status-detail__k">Source job</span>
                        <span class="install-status-detail__v install-status-package">{{ item.sourceJobCode }}</span>
                      </div>
                    </div>
                  </td>
                </template>
              </v-data-table>
            </div>
          </div>
        </v-container>
      </v-card-text>

      <v-divider />
      <v-card-actions class="install-status-actions px-4 py-3">
        <v-spacer />
        <v-btn
          text
          @click="dialog = false"
        >
          Close
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
  import moment from 'moment'

  const toastOptions = { position: 'topRight' }

  export default {
    name: 'AppInstallationStatusDialog',
    props: {
      value: {
        type: Boolean,
        default: false,
      },
      selectedCompanyId: {
        type: [Number, String],
        default: 0,
      },
    },
    data () {
      return {
        dialog: this.value,
        loadingDevices: false,
        devices: [],
        deviceId: null,
        loadingApps: false,
        packageOptions: [],
        packageName: null,
        loadingStatus: false,
        statusError: '',
        hasSearched: false,
        deviceReports: [],
        showAllApps: true,
        appHeaders: [
          { text: 'App', value: 'appName' },
          { text: 'Version', value: 'versionName' },
          { text: 'Status', value: 'lastStatus' },
          { text: 'Reported', value: 'reportedAt' },
          { text: '', value: 'data-table-expand' },
        ],
      }
    },
    computed: {
      deviceOptions () {
        return this.devices.map(device => ({
          ...device,
          displayLabel: this.buildDeviceLabel(device),
        }))
      },
      whitelistSet () {
        return new Set(
          this.packageOptions.map(pkg => String(pkg || '').trim().toLowerCase()),
        )
      },
    },
    watch: {
      value (val) {
        this.dialog = val
        if (val) {
          this.onOpen()
        }
      },
      dialog (val) {
        this.$emit('input', val)
      },
      selectedCompanyId () {
        if (this.dialog) {
          this.onOpen()
        }
      },
    },
    methods: {
      onOpen () {
        this.resetState()
        this.loadDevices()
        this.loadApps()
        // Show every device's installation status up front; the filters above
        // only narrow the list when the user wants a specific device/package.
        this.loadStatus()
      },
      resetState () {
        this.deviceId = null
        this.packageName = null
        this.statusError = ''
        this.hasSearched = false
        this.deviceReports = []
        this.showAllApps = true
      },
      hasNumericId (value) {
        return Number.isFinite(Number(value)) && Number(value) > 0
      },
      isFiniteNumber (value) {
        return value !== null && value !== undefined && value !== '' && Number.isFinite(Number(value))
      },
      buildDeviceLabel (device) {
        const name = device.name || 'Unnamed device'
        return device.macAddress ? `${name} - ${device.macAddress}` : name
      },
      async loadDevices () {
        if (!this.selectedCompanyId) {
          this.devices = []
          return
        }

        this.loadingDevices = true
        try {
          const response = await this.$http.get('Company/DevicePaginationByCompany', {
            params: {
              companyId: this.selectedCompanyId,
              page: 1,
              limit: 1000,
            },
          })
          this.devices = (Array.isArray(response.data) ? response.data : [])
            .filter(device => this.hasNumericId(device?.id))
        } catch (error) {
          this.devices = []
        } finally {
          this.loadingDevices = false
        }
      },
      async loadApps () {
        if (!this.selectedCompanyId) {
          this.packageOptions = []
          return
        }

        this.loadingApps = true
        try {
          const response = await this.$http.post(`/CompanyKioskApp/paginated/${this.selectedCompanyId}`, {
            pageNumber: 1,
            pageSize: 500,
          })

          const items = response.data?.data?.items || []
          this.packageOptions = [...new Set(
            items
              .map(app => String(app?.packageName || '').trim())
              .filter(Boolean),
          )]
        } catch (error) {
          this.packageOptions = []
        } finally {
          this.loadingApps = false
        }
      },
      buildParams () {
        const params = { companyId: Number(this.selectedCompanyId) }

        if (this.hasNumericId(this.deviceId)) {
          params.deviceId = Number(this.deviceId)
          const device = this.devices.find(d => d.id === this.deviceId)
          if (device?.macAddress) {
            params.macAddress = device.macAddress
          }
        }

        const pkg = String(this.packageName || '').trim()
        if (pkg) {
          params.packageName = pkg
        }

        return params
      },
      async loadStatus () {
        if (!this.selectedCompanyId) {
          this.$toast.error('Select a company first.', 'Error', toastOptions)
          return
        }

        this.loadingStatus = true
        this.statusError = ''
        this.hasSearched = true
        try {
          const response = await this.$http.get('AppInstallation/InstalledApks', {
            params: this.buildParams(),
          })
          this.deviceReports = this.normalizeReports(response.data)
        } catch (error) {
          this.deviceReports = []
          this.statusError = error?.response?.data?.message ||
            error?.message ||
            'Could not load installation status.'
        } finally {
          this.loadingStatus = false
        }
      },
      normalizeReports (raw) {
        let list = []
        if (Array.isArray(raw)) {
          list = raw
        } else if (Array.isArray(raw?.data)) {
          list = raw.data
        } else if (raw?.data && typeof raw.data === 'object') {
          list = [raw.data]
        } else if (raw && typeof raw === 'object') {
          list = [raw]
        }

        // Group every record by its device so a device shows as a single card,
        // merging apps from records that repeat the same device.
        const groups = new Map()

        list
          .filter(report => report && typeof report === 'object')
          .forEach((report) => {
            const key = String(
              report.deviceId ?? report.macAddress ?? `device-${groups.size}`,
            ).toLowerCase()

            if (!groups.has(key)) {
              const device = this.devices.find(
                d => d.id === report.deviceId ||
                  (report.macAddress && d.macAddress === report.macAddress),
              )
              groups.set(key, {
                deviceId: report.deviceId,
                macAddress: report.macAddress || device?.macAddress || '',
                deviceName: report.deviceName || device?.name || '',
                reportedAt: '',
                _key: key,
                apps: [],
                _appByPackage: new Map(),
              })
            }

            const group = groups.get(key)

            if (report.deviceName && !group.deviceName) group.deviceName = report.deviceName
            // Track the latest report time across this device's app records.
            if (report.reportedAt && report.reportedAt > group.reportedAt) {
              group.reportedAt = report.reportedAt
            }

            // Apps may arrive nested under `apps`, or the record itself may be a
            // single flat app entry (has a packageName at the top level).
            const apps = Array.isArray(report.apps)
              ? report.apps
              : (report.packageName ? [report] : [])

            apps
              .filter(app => app && typeof app === 'object')
              .forEach((app) => {
                const pkgKey = String(app.packageName || app.id || '').toLowerCase()
                const existing = group._appByPackage.get(pkgKey)
                // Keep only the most recently reported record per package.
                if (!existing || String(app.reportedAt || '') > String(existing.reportedAt || '')) {
                  group._appByPackage.set(pkgKey, app)
                }
              })
          })

        return [...groups.values()].map(({ _appByPackage, ...group }) => ({
          ...group,
          apps: [..._appByPackage.values()],
        }))
      },
      isWhitelisted (packageName) {
        return this.whitelistSet.has(String(packageName || '').trim().toLowerCase())
      },
      countWhitelisted (report) {
        return (report.apps || []).filter(app => this.isWhitelisted(app.packageName)).length
      },
      shortDateTime (value) {
        if (!value) {
          return '-'
        }
        // Backend sends naive UTC timestamps; convert to local like $format.dateTime
        // so this column stays consistent with the detail dates.
        const m = moment.utc(value)
        return m.isValid() ? m.local().format('DD MMM YYYY, h:mm a') : '-'
      },
      visibleApps (report) {
        // Flag each app, hide non-whitelisted when the toggle is off, and always
        // float whitelisted apps to the top.
        const apps = (report.apps || []).map(app => ({
          ...app,
          _whitelisted: this.isWhitelisted(app.packageName),
        }))

        const filtered = this.showAllApps ? apps : apps.filter(app => app._whitelisted)

        return filtered.sort((left, right) => {
          if (Number(right._whitelisted) !== Number(left._whitelisted)) {
            return Number(right._whitelisted) - Number(left._whitelisted)
          }
          return String(left.packageName || '').localeCompare(String(right.packageName || ''))
        })
      },
      isValidDate (value) {
        if (!value) {
          return false
        }
        const m = moment.utc(value)
        // Treat epoch/placeholder dates (e.g. 2009-01-01 sentinels) as empty.
        return m.isValid() && m.year() > 2010
      },
      statusColor (status) {
        switch (String(status || '').trim().toLowerCase()) {
          case 'installed':
          case 'success':
          case 'completed':
            return 'success'
          case 'pending':
          case 'installing':
          case 'queued':
            return 'orange'
          case 'failed':
          case 'error':
            return 'error'
          default:
            return 'grey'
        }
      },
    },
  }
</script>

<style scoped>
  .install-status-card {
    display: flex;
    flex-direction: column;
    max-height: 88vh;
    overflow: hidden;
  }

  .v-dialog--fullscreen .install-status-card {
    max-height: 100vh;
    height: 100vh;
  }

  .install-status-header {
    flex: 0 0 auto;
    display: flex;
    align-items: center;
    gap: 14px;
    padding: 14px 16px;
  }

  .install-status-header__icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 46px;
    height: 46px;
    flex: 0 0 auto;
    border-radius: 12px;
    background: rgba(255, 255, 255, 0.18);
  }

  .install-status-header__icon >>> .v-icon {
    font-size: 28px;
  }

  .install-status-header__text {
    min-width: 0;
    flex: 1 1 auto;
  }

  .install-status-header__title {
    font-size: 1.35rem;
    font-weight: 600;
    line-height: 1.3;
    letter-spacing: normal;
    text-transform: none;
  }

  .install-status-header__subtitle {
    color: rgba(255, 255, 255, 0.88);
    font-size: 0.9rem;
    line-height: 1.35;
    letter-spacing: normal;
    text-transform: none;
    white-space: normal;
    margin-top: 3px;
  }

  .install-status-header__close {
    flex: 0 0 auto;
    align-self: flex-start;
  }

  .install-status-body {
    flex: 1 1 auto;
    min-height: 0;
    overflow-y: auto;
    background: #fafafa;
    padding-top: 20px;
  }

  .install-status-section {
    background: #ffffff;
    border: 1px solid #ececec;
    border-radius: 12px;
  }

  .install-status-toolbar {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 6px 16px;
    padding: 8px 12px;
    background: #ffffff;
    border: 1px solid #ececec;
    border-radius: 12px;
  }

  .install-status-section-title {
    font-size: 1.15rem;
    font-weight: 600;
    color: rgba(0, 0, 0, 0.85);
  }

  .install-status-device {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 12px;
  }

  .install-status-device__main {
    display: flex;
    align-items: center;
    flex: 1 1 auto;
    min-width: 0;
  }

  .install-status-device__name {
    font-size: 1.05rem;
    font-weight: 600;
  }

  .install-status-device__id {
    font-family: 'Roboto Mono', monospace;
    font-size: 0.85rem;
    color: rgba(0, 0, 0, 0.6);
  }

  .install-status-device__meta {
    display: flex;
    flex-wrap: wrap;
    gap: 16px;
  }

  .install-status-meta {
    display: flex;
    align-items: center;
    gap: 4px;
    font-size: 0.9rem;
    color: rgba(0, 0, 0, 0.7);
  }

  .install-status-package {
    font-family: 'Roboto Mono', monospace;
    font-size: 0.8rem;
    color: rgba(0, 0, 0, 0.55);
  }

  .install-status-app-cell {
    min-width: 0;
  }

  .install-status-app-name {
    font-weight: 600;
    font-size: 0.98rem;
    color: rgba(0, 0, 0, 0.85);
  }

  .install-status-ver {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 4px 8px;
  }

  .install-status-ver__name {
    display: inline-block;
    max-width: 160px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    vertical-align: bottom;
  }

  .install-status-ver__code {
    font-size: 0.78rem;
    color: rgba(0, 0, 0, 0.45);
  }

  .install-status-ver__chip {
    height: 18px;
  }

  .install-status-reported {
    white-space: nowrap;
    font-size: 0.85rem;
    color: rgba(0, 0, 0, 0.7);
  }

  .install-status-app-table >>> tbody td {
    font-size: 0.95rem;
    height: 52px;
  }

  .install-status-app-table >>> thead th,
  .install-status-app-table >>> tbody td {
    white-space: nowrap;
  }

  .install-status-detail {
    background: #f7f9fc;
    padding: 14px 20px !important;
  }

  .install-status-detail-grid {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 8px 24px;
  }

  .install-status-detail-row {
    display: flex;
    flex-direction: column;
  }

  .install-status-detail-row--full {
    grid-column: 1 / -1;
  }

  .install-status-detail__k {
    font-size: 0.72rem;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: rgba(0, 0, 0, 0.5);
  }

  .install-status-detail__v {
    font-size: 0.92rem;
    color: rgba(0, 0, 0, 0.82);
    word-break: break-word;
  }

  .install-status-actions {
    flex: 0 0 auto;
  }
</style>
